import { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { CardInfo } from '@/components'
import { formatCurrency, formatLongDate, isSaleOrderEditable } from '@/helpers'
import { LogisticShipmentStatusEnum } from '@/types'
import {
  Button,
  EditIcon,
  InfoIcon,
  PinIcon,
  Tag,
  Text,
  Tooltip,
} from '@mercai/clever'

import type { CardOrderLogisticProps } from './card-order-logistic.type'

import { Link } from 'react-router-dom'
import styles from './card-order-logistic.module.css'

export const CardOrderLogistic = ({
  isLoading,
  shipment,
  onEdit = () => null,
  saleOrderStatus,
}: CardOrderLogisticProps) => {
  const { t: tEnums } = useTranslation('enums')
  const { t } = useTranslation('componentCardsOrderCenterShow')

  const isEditable = isSaleOrderEditable(saleOrderStatus)

  const statusShipmentObject: Record<LogisticShipmentStatusEnum, ReactNode> = {
    pending: (
      <Tag model="light" variant="warning">
        {tEnums('logistic_shipment_status_pending')}
      </Tag>
    ),
    delivered: (
      <Tag model="light" variant="success">
        {tEnums('logistic_shipment_status_delivered')}
      </Tag>
    ),
    delivery_scheduled: (
      <Tag model="light" variant="primary">
        {tEnums('logistic_shipment_status_delivery_scheduled')}
      </Tag>
    ),
    in_transit: (
      <Tag model="light" variant="primary">
        {tEnums('logistic_shipment_status_in_transit')}
      </Tag>
    ),
    partially_delivered: (
      <Tag model="light" variant="warning">
        {tEnums('logistic_shipment_status_partially_delivered')}
      </Tag>
    ),
    on_hold: (
      <Tag model="light" variant="warning">
        {tEnums('logistic_shipment_status_on_hold')}
      </Tag>
    ),
    refused: (
      <Tag model="light" variant="danger">
        {tEnums('logistic_shipment_status_refused')}
      </Tag>
    ),
    cancelled: (
      <Tag model="light" variant="danger">
        {tEnums('logistic_shipment_status_cancelled')}
      </Tag>
    ),
  }

  const getAddress = () => {
    return shipment?.destinationAddress ? (
      <>
        <Text variant="text-base">
          {shipment?.destinationAddress}{' '}
          {shipment?.destinationAddressMapsUrl ? (
            <Link
              target="_blank"
              to={shipment?.destinationAddressMapsUrl || ''}
              className={styles.link}
            >
              Google Maps
            </Link>
          ) : null}
        </Text>
      </>
    ) : (
      t('orderLogisticShipmentAddressNotInformed')
    )
  }

  return (
    <CardInfo
      isLoading={isLoading}
      icon={<PinIcon />}
      title={t('orderLogisticTitle')}
      actions={
        isEditable && (
          <Button
            size="small"
            variant="ghost"
            className={styles.edit}
            onClick={onEdit}
          >
            {t('editLabel')}
            <EditIcon />
          </Button>
        )
      }
      items={[
        {
          label: t('orderLogisticShipmentStatus'),
          value: statusShipmentObject[shipment?.status || 'pending'],
        },
        {
          label: t('orderLogisticShipmentDeliveryOption'),
          value: shipment?.deliveryOption
            ? tEnums(
                `logistic_shipment_delivery_option_${shipment?.deliveryOption}`,
              )
            : '-',
        },
        {
          label: t('orderLogisticShipmentAddress'),
          value: getAddress(),
        },
        {
          label: (
            <div className={styles['label-row']}>
              {t('orderLogisticShipmentExpectedDeliveryDays')}
              <Tooltip
                text={t('orderLogisticShipmentExpectedDeliveryDescription')}
                widthText="16rem"
                orientation="top"
              >
                <InfoIcon className={styles['info-icon']} />
              </Tooltip>
            </div>
          ),
          value: shipment?.expectedDeliveryDays
            ? `${shipment?.expectedDeliveryDays} ${t('orderLogisticShipmentExpectedDeliveryDaysSuffix')}`
            : '-',
        },
        {
          label: (
            <div className={styles['label-row']}>
              {t('orderLogisticShipmentExpectedDeliveryDate')}
              <Tooltip
                text={t('orderLogisticShipmentExpectedDeliveryDateDescription')}
                widthText="16rem"
                orientation="top"
              >
                <InfoIcon className={styles['info-icon']} />
              </Tooltip>
            </div>
          ),
          value: shipment?.expectedDeliveryAt
            ? formatLongDate(shipment?.expectedDeliveryDate)
            : '-',
        },
        {
          label: (
            <div className={styles['label-row']}>
              {t('orderLogisticShipmentExpectedDeliveryAt')}
              <Tooltip
                text={t('orderLogisticShipmentExpectedDeliveryAtDescription')}
                widthText="16rem"
                orientation="top"
              >
                <InfoIcon className={styles['info-icon']} />
              </Tooltip>
            </div>
          ),
          value: shipment?.expectedDeliveryAt
            ? formatLongDate(shipment?.expectedDeliveryAt)
            : '-',
        },
        {
          label: (
            <div className={styles['label-row']}>
              {t('orderLogisticShipmentDeliveryAt')}
              <Tooltip
                text={t('orderLogisticShipmentDeliveryAtDescription')}
                widthText="16rem"
                orientation="top"
              >
                <InfoIcon className={styles['info-icon']} />
              </Tooltip>
            </div>
          ),
          value: shipment?.deliveredAt
            ? formatLongDate(shipment?.deliveredAt)
            : '-',
        },
        {
          label: t('orderLogisticShipmentTotalFreightPrice'),
          value: shipment?.price
            ? formatCurrency(shipment?.price, 'es-MX', 'USD')
            : '-',
        },
      ]}
    />
  )
}
